import type { Product } from '../algolia/interfaces/product';
import type { Facet } from './facets/interfaces/facets';
import type { PlainQueryParams } from './interfaces/algolia';
import type { FilterConfig } from './interfaces/filterConfig';
import type { ListingPage, ProcessedResult } from './model';
import type { CategoriesList } from './technicalFilters/utils/generateTechnicalFiltersQuery';

export const START_LISTING = 'productDiscovery/Listing/START';
export const STOP_LISTING = 'productDiscovery/Listing/STOP';

export const INITIAL_SEARCH = 'productDiscovery/Listing/INITIAL_SEARCH';
export const SEARCHING = 'productDiscovery/Listing/SEARCHING';
export const SEARCH_ONCE = 'productDiscovery/Listing/SEARCH_ONCE';
export const SEARCH_SUCCESS = 'productDiscovery/Listing/SEARCH_SUCCESS';
export const SEARCH_FAILED = 'productDiscovery/Listing/SEARCH_FAILED';

export const SET_PAGE = 'productDiscovery/Listing/SET_PAGE';
export const SET_URL = 'productDiscovery/Listing/SET_URL';
export const SET_FILTERS_CONFIG = 'productDiscovery/Listing/SET_FILTERS_CONFIG';
export const SET_CUSTOM_FACET = 'productDiscovery/Listing/SET_CUSTOM_FACET';
export const SET_CATEGORIES_LIST =
  'productDiscovery/Listing/SET_CATEGORIES_LIST';
export const SET_PREVIOUS_SELECTED_CAT_ID =
  'productDiscovery/Listing/SET_PREVIOUS_SELECTED_CAT_ID';
export const SEARCH_IN_FACET_VALUES =
  'productDiscovery/Listing/SEARCH_IN_FACET_VALUES';
export const SEARCH_FOR_FACET_VALUES_SUCCESS =
  'productDiscovery/Listing/SEARCH_FOR_FACET_VALUES_SUCCESS';
export const SEARCH_FOR_FACET_VALUES_FAILED =
  'productDiscovery/Listing/SEARCH_FOR_FACET_VALUES_FAILED';

export const SET_PAGINATION_PAGE =
  'productDiscovery/Listing/SET_PAGINATION_PAGE';

export const SET_RERANKING_COUNT =
  'productDiscovery/Listing/SET_RERANKING_COUNT';

export const SET_INSPECT_LISTINGS =
  'productDiscovery/Listing/SET_INSPECT_LISTING';

export interface StartListing {
  type: typeof START_LISTING;
  initialSearch: boolean;
}

export const startListing = (initialSearch = false): StartListing => ({
  type: START_LISTING,
  initialSearch,
});

export interface InitialSearch {
  type: typeof INITIAL_SEARCH;
}

export const initialSearch = (): InitialSearch => ({
  type: INITIAL_SEARCH,
});

export interface Searching {
  type: typeof SEARCHING;
  queryParams: PlainQueryParams | undefined;
}

export const searching = (
  queryParams: Searching['queryParams'],
): Searching => ({
  type: SEARCHING,
  queryParams,
});

export interface SearchOnce {
  type: typeof SEARCH_ONCE;
  index: string;
  queryParams: PlainQueryParams;
}

export const searchOnce = (
  index: SearchOnce['index'],
  queryParams: SearchOnce['queryParams'],
): SearchOnce => ({
  type: SEARCH_ONCE,
  index,
  queryParams,
});

export interface SearchSuccess<P extends Product> {
  type: typeof SEARCH_SUCCESS;
  result: ProcessedResult<P>;
}

export const searchSuccess = <P extends Product>(
  result: SearchSuccess<P>['result'],
): SearchSuccess<P> => ({
  type: SEARCH_SUCCESS,
  result,
});

export interface SetInspectListings {
  type: typeof SET_INSPECT_LISTINGS;
  inspectListings: boolean;
}

export const setInspectListings = (
  inspectListings: boolean,
): SetInspectListings => ({
  type: SET_INSPECT_LISTINGS,
  inspectListings,
});

export interface SearchFailed {
  type: typeof SEARCH_FAILED;
  error: Error & { status: number };
}

export const searchFailed = (error: SearchFailed['error']): SearchFailed => ({
  type: SEARCH_FAILED,
  error,
});

export interface SetPage {
  type: typeof SET_PAGE;
  pageName: ListingPage | null;
}

export const setPage = (pageName: ListingPage | null): SetPage => ({
  type: SET_PAGE,
  pageName,
});

export interface SetURL {
  type: typeof SET_URL;
  url: string;
}

export const setURL = (url: string): SetURL => ({
  type: SET_URL,
  url,
});

export interface StopListing {
  type: typeof STOP_LISTING;
}

export const stopListing = (): StopListing => ({ type: STOP_LISTING });

export interface SetFiltersConfig {
  type: typeof SET_FILTERS_CONFIG;
  filtersConfig: FilterConfig[];
}

export const setFiltersConfig = (
  filtersConfig: FilterConfig[],
): SetFiltersConfig => ({
  type: SET_FILTERS_CONFIG,
  filtersConfig,
});

export interface SetCustomFacet {
  type: typeof SET_CUSTOM_FACET;
  facetName: string;
  facet: Facet;
}

export const setCustomFacet = (
  facetName: string,
  facet: Facet,
): SetCustomFacet => ({
  type: SET_CUSTOM_FACET,
  facetName,
  facet,
});

export interface SearchInFacetValues {
  type: typeof SEARCH_IN_FACET_VALUES;
  facetName: string;
  query: string;
}

export const searchInFacetValues = (
  facetName: string,
  query: string,
): SearchInFacetValues => ({
  type: SEARCH_IN_FACET_VALUES,
  facetName,
  query,
});

export interface SearchForFacetValuesSuccess {
  type: typeof SEARCH_FOR_FACET_VALUES_SUCCESS;
  facetName: string;
  facet: Facet;
}

export const searchForFacetValuesSuccess = (
  facetName: string,
  facet: Facet,
): SearchForFacetValuesSuccess => ({
  type: SEARCH_FOR_FACET_VALUES_SUCCESS,
  facetName,
  facet,
});

export interface SearchForFacetValuesFailed {
  type: typeof SEARCH_FOR_FACET_VALUES_FAILED;
  error: Error & { status?: number };
}

export const searchForFacetValuesFailed = (
  error: Error & { status?: number },
): SearchForFacetValuesFailed => ({
  type: SEARCH_FOR_FACET_VALUES_FAILED,
  error,
});

export interface SetRerankingCount {
  type: typeof SET_RERANKING_COUNT;
  rerankingCount: number;
  searchEngineProxy: boolean;
  ruleContexts?: string[];
}

export const setRerankingCount = (
  rerankingCount: number,
  searchEngineProxy: boolean,
  ruleContexts?: string[],
): SetRerankingCount => ({
  type: SET_RERANKING_COUNT,
  rerankingCount,
  searchEngineProxy,
  ruleContexts,
});

export interface SetCategoriesList {
  type: typeof SET_CATEGORIES_LIST;
  categoriesList: CategoriesList;
}

export const setCategoriesList = (
  categoriesList: CategoriesList,
): SetCategoriesList => ({
  type: SET_CATEGORIES_LIST,
  categoriesList,
});

export type Action<P extends Product = Product> =
  | Searching
  | SearchOnce
  | SearchSuccess<P>
  | SearchFailed
  | SetPage
  | SetURL
  | SetFiltersConfig
  | SetCustomFacet
  | SearchForFacetValuesSuccess
  | SearchForFacetValuesFailed
  | SetRerankingCount
  | SetCategoriesList
  | SetInspectListings;
